import { teamPaths, type SlotCtx } from "./bracket-graph";
import { hookupsOf } from "./game-story";
import type { Identity } from "./identity";
import { playerGameLines, type PlayerGameLine } from "./player-stats";
import { teamGames, teamRecord } from "./team-hub";
import { timeKey } from "./time";
import {
  gameSlots,
  resolveSlot,
  type GameState,
  type GameStats,
  type LabelCtx,
  type ScheduledGame,
  type Slot,
  type Team,
  UNASSIGNED_DIVISION,
} from "./types";

export interface ScheduleFilters {
  day?: string;
  team?: string;
  division?: string;
  field?: string;
}

/** Small, bounded URL state; unknown keys never become filters. */
export function scheduleFilters(search: Record<string, unknown>): ScheduleFilters {
  const out: ScheduleFilters = {};
  for (const key of ["day", "team", "division", "field"] as const) {
    const value = search[key];
    if (typeof value === "string" && value.length > 0 && value.length <= 120) out[key] = value;
  }
  return out;
}

function divisionOf(game: ScheduledGame, ctx: SlotCtx): string {
  if (game.divisionId !== undefined) return game.divisionId;
  const slots = gameSlots(game);
  const a = resolveSlot(slots.a, ctx)?.divisionId;
  const b = resolveSlot(slots.b, ctx)?.divisionId;
  return a !== undefined && a === b ? a : UNASSIGNED_DIVISION;
}

/** A team filter means confirmed participation, never a guessed pool seed. */
export function filterSchedule(
  games: readonly ScheduledGame[],
  filters: ScheduleFilters,
  ctx: SlotCtx,
): ScheduledGame[] {
  return games
    .filter((game) => {
      const slots = gameSlots(game);
      return (
        (!filters.day || game.startAt.slice(0, 10) === filters.day) &&
        (!filters.field || game.fieldId === filters.field) &&
        (!filters.division || divisionOf(game, ctx) === filters.division) &&
        (!filters.team ||
          [slots.a, slots.b].some((slot) => resolveSlot(slot, ctx)?.id === filters.team))
      );
    })
    .sort((a, b) => timeKey(a.startAt) - timeKey(b.startAt) || a.id.localeCompare(b.id));
}

/** Compare what was known BEFORE this fixture, within its division. */
export function matchPreview(game: ScheduledGame, ctx: SlotCtx, labelCtx: LabelCtx) {
  const slots = gameSlots(game);
  const a = resolveSlot(slots.a, ctx);
  const b = resolveSlot(slots.b, ctx);
  if (a === null || b === null) return null;
  const prior = ctx.schedule.filter(
    (g) =>
      g.id !== game.id &&
      g.result !== undefined &&
      timeKey(g.startAt) < timeKey(game.startAt) &&
      divisionOf(g, ctx) === divisionOf(game, ctx),
  );
  const side = (team: Team) => {
    const games = teamGames({ teamId: team.id, schedule: prior, ctx, labelCtx });
    return { team, games, record: teamRecord(games), form: games.slice(-5) };
  };
  const left = side(a);
  const right = side(b);
  const opponents = new Set(right.games.map((g) => g.opponentId));
  const common = [...new Set(left.games.map((g) => g.opponentId))]
    .filter((id): id is string => id !== null && opponents.has(id))
    .map((id) => ({
      teamId: id,
      a: left.games.filter((g) => g.opponentId === id),
      b: right.games.filter((g) => g.opponentId === id),
    }));
  return { a: left, b: right, common };
}

/** Pool fixtures plus the existing bracket graph, including conditional seed
 * entry while pools are unfinished. Branches stay conditional all the way down. */
export function tournamentProgression(team: Team, ctx: SlotCtx) {
  const paths = teamPaths(ctx.schedule, team.id, ctx);
  const possible = new Set(paths.possible);
  const candidates = ctx.schedule.filter((g) => g.stage === "bracket");
  const seedCouldBeUs = (slot: Slot | null): boolean =>
    slot?.kind === "seed" &&
    resolveSlot(slot, ctx) === null &&
    slot.divisionId === team.divisionId &&
    (slot.poolId === undefined || slot.poolId === team.poolId) &&
    slot.rank <=
      ctx.teams.filter(
        (t) =>
          t.divisionId === slot.divisionId &&
          (slot.poolId === undefined || t.poolId === slot.poolId),
      ).length;
  for (const game of candidates) {
    const slots = gameSlots(game);
    if (game.result === undefined && [slots.a, slots.b].some(seedCouldBeUs)) possible.add(game.id);
  }
  // A bounded fixed point also tolerates a malformed cyclic bracket.
  for (let pass = 0; pass < candidates.length; pass += 1) {
    const before = possible.size;
    for (const game of candidates) {
      const slots = gameSlots(game);
      if (
        game.result === undefined &&
        [slots.a, slots.b].some(
          (slot) =>
            slot !== null &&
            (slot.kind === "winner" || slot.kind === "loser") &&
            possible.has(slot.gameId),
        )
      )
        possible.add(game.id);
    }
    if (possible.size === before) break;
  }
  return ctx.schedule
    .flatMap((game) => {
      const slots = gameSlots(game);
      const confirmed =
        paths.confirmed.has(game.id) ||
        [slots.a, slots.b].some((slot) => resolveSlot(slot, ctx)?.id === team.id);
      return confirmed || possible.has(game.id) ? [{ game, confirmed }] : [];
    })
    .sort(
      (a, b) =>
        timeKey(a.game.startAt) - timeKey(b.game.startAt) || a.game.id.localeCompare(b.game.id),
    );
}

/** Only currently connected, active scheduled games count as live. Old final
 * cards and stale sockets must never manufacture activity. */
export function tournamentPulse(
  schedule: readonly ScheduledGame[],
  feeds: readonly {
    connected: boolean;
    state: Pick<GameState, "period" | "scheduleGameId"> | null;
  }[],
) {
  const finished = schedule.filter((g) => g.result !== undefined);
  const remaining = new Set(schedule.filter((g) => g.result === undefined).map((g) => g.id));
  const live = new Set(
    feeds.flatMap(({ connected, state }) =>
      connected &&
      state !== null &&
      state.scheduleGameId !== null &&
      remaining.has(state.scheduleGameId) &&
      (state.period === "h1" || state.period === "half" || state.period === "h2")
        ? [state.scheduleGameId]
        : [],
    ),
  );
  return {
    total: schedule.length,
    completed: finished.length,
    live: live.size,
    goals: finished.reduce((sum, g) => sum + (g.result?.scoreA ?? 0) + (g.result?.scoreB ?? 0), 0),
  };
}

/** The UI's cutoff never turns an equal sporting result into a lower rank.
 * Input is already sorted by this value, descending. */
export function topWithTies<T>(rows: readonly T[], value: (row: T) => number, limit = 3): T[] {
  if (limit <= 0) return [];
  const cutoff = rows[limit - 1];
  return rows.filter((row, i) => i < limit || (cutoff !== undefined && value(row) === value(cutoff)));
}

/** Highlights require a finished, reconciled snapshot with the same sides.
 * Reopened, rewired, orphaned and corrected-but-stale records are excluded. */
export function tournamentHighlights(
  ctx: SlotCtx,
  stats: readonly GameStats[],
  day = "",
  identity?: Identity,
) {
  const games = ctx.schedule.filter(
    (g) => g.result !== undefined && (!day || g.startAt.slice(0, 10) === day),
  );
  const snapshots = stats.filter((s) => {
    const game = games.find((g) => g.id === s.scheduleGameId);
    if (game?.result === undefined) return false;
    const slots = gameSlots(game);
    return (
      s.teamA.teamId !== null &&
      s.teamB.teamId !== null &&
      s.teamA.teamId === resolveSlot(slots.a, ctx)?.id &&
      s.teamB.teamId === resolveSlot(slots.b, ctx)?.id &&
      s.scoreA === game.result.scoreA &&
      s.scoreB === game.result.scoreB &&
      s.points.filter((p) => p.team === "A").length === s.scoreA &&
      s.points.filter((p) => p.team === "B").length === s.scoreB
    );
  });
  // Explicit identity merges can join two entries on the same frozen roster.
  // Sum credits once per person/game/side, retaining one copy of coverage.
  const people = new Map<string, PlayerGameLine>();
  for (const line of playerGameLines(snapshots)) {
    const playerId = identity?.idOf(line.playerId) ?? line.playerId;
    const key = `${line.gameId}:${line.side}:${playerId}`;
    const existing = people.get(key);
    if (existing) {
      existing.goals += line.goals;
      existing.assists += line.assists;
      existing.breakGoals += line.breakGoals;
      existing.breakAssists += line.breakAssists;
      existing.gameWinners += line.gameWinners;
      existing.universeGoals += line.universeGoals;
    } else {
      people.set(key, {
        ...line,
        playerId,
        frozenName: identity?.nameOf(playerId) ?? line.frozenName,
      });
    }
  }
  const performances = [...people.values()]
    .filter((p) => p.goals + p.assists > 0)
    .sort(
      (a, b) =>
        b.goals + b.assists - a.goals - a.assists ||
        a.gameId.localeCompare(b.gameId) ||
        a.playerId.localeCompare(b.playerId),
    );
  const pairs = snapshots
    .flatMap((s) =>
      (["A", "B"] as const).flatMap((side) => {
        const team = side === "A" ? s.teamA : s.teamB;
        const roster = new Map(
          (team.roster ?? []).map((p) => {
            const id = identity?.idOf(p.id) ?? p.id;
            return [id, { ...p, id, name: identity?.nameOf(id) ?? p.name }];
          }),
        );
        const personId = (id: string | null) =>
          id !== null && team.roster?.some((p) => p.id === id) ? (identity?.idOf(id) ?? id) : null;
        const points = s.points
          .filter((p) => p.team === side)
          .map((p) => ({ ...p, scorerId: personId(p.scorerId), assistId: personId(p.assistId) }));
        return hookupsOf(points, side).flatMap((pair) => {
          const scorer = roster.get(pair.scorerId);
          const assist = roster.get(pair.assistId);
          return scorer && assist
            ? [{ gameId: s.scheduleGameId, team: team.name, scorer, assist, count: pair.count }]
            : [];
        });
      }),
    )
    .sort((a, b) => b.count - a.count || a.gameId.localeCompare(b.gameId));
  const closeGames = games
    .filter((g) => g.result !== undefined && Math.abs(g.result.scoreA - g.result.scoreB) <= 2)
    .sort((a, b) => timeKey(b.startAt) - timeKey(a.startAt));
  return { games: games.length, recorded: snapshots.length, performances: topWithTies(performances, (p) => p.goals + p.assists), pairs: topWithTies(pairs, (p) => p.count), closeGames };
}
